import { v, type Infer } from "convex/values";

/** Which name found the words at the provider: the pipeline's identify ports
 * for an upload, the owner's correction or pick for a relyric. */
const candidate = v.union(
  v.literal("fingerprint"),
  v.literal("match"),
  v.literal("cleaned"),
  v.literal("typed"),
  v.literal("corrected"),
  v.literal("picked"),
);

/** What the lyric-text port reported beside the words it left for alignment
 * (pipeline lyrics_fetch): the provider, which candidate it answered for,
 * and for LRCLIB the entry and how far its duration sat from the track's. */
export const vFetchedLyrics = v.object({
  provider: v.union(v.literal("lrclib"), v.literal("genius")),
  candidate,
  lrclibId: v.optional(v.number()),
  synced: v.optional(v.boolean()),
  durationDeltaMs: v.optional(v.number()),
});

export type FetchedLyrics = Infer<typeof vFetchedLyrics>;

/** Alignment's measure of how the text fitted the singing: the share of words
 * it placed, the mean word probability, and whether that cleared its floors. */
export const vLyricsFit = v.object({
  placed: v.number(),
  meanProbability: v.number(),
  accepted: v.boolean(),
});

export type LyricsFit = Infer<typeof vLyricsFit>;

/**
 * Where a track's words came from, stamped by the workflow that materialised
 * them. `transcription` with `unverified` means the text we had did not fit
 * the singing, so the vocal was transcribed instead; `fit` keeps the measure.
 * Pasted words carry only the provider: the owner is their source.
 */
export const vLyricsSource = v.object({
  provider: v.union(
    v.literal("lrclib"),
    v.literal("genius"),
    v.literal("paste"),
    v.literal("transcription"),
  ),
  candidate: v.optional(candidate),
  lrclibId: v.optional(v.number()),
  synced: v.optional(v.boolean()),
  durationDeltaMs: v.optional(v.number()),
  fit: v.optional(v.object({ placed: v.number(), meanProbability: v.number() })),
  unverified: v.optional(v.boolean()),
});

export type LyricsSource = Infer<typeof vLyricsSource>;

/** The words alignment was given, before it ran: fetched, pasted, or none. */
export type LyricsText = FetchedLyrics | { provider: "paste" } | null;

/** The source to stamp on a track from the two pipeline reports: what text
 * alignment was given and how it fitted. No text, or text the aligner never
 * saw, is a plain transcription; text that did not fit is an unverified one. */
export function lyricsSourceOf(text: LyricsText, fit: LyricsFit | null): LyricsSource {
  if (!text || !fit) return { provider: "transcription" };
  const measured = { placed: fit.placed, meanProbability: fit.meanProbability };
  if (!fit.accepted) return { provider: "transcription", unverified: true, fit: measured };
  return { ...text, fit: measured };
}
